/* eslint-disable @typescript-eslint/triple-slash-reference */
/// <reference path="../../types/global.d.ts" />
import { headerName } from "@/nav-localized/config";
import { BUITheme } from "@blofin/blofin-ui";
import { FC, useEffect, useState } from "react";
import Header from "../Header";
import { createHeader } from "../Header/entry";
import type { HeaderProps, UserInfo } from "../Header/types";
import useNav from "./useNav";

const NavHeader: FC<{
  apiConfig: { baseUrl: string };
  showTheme: boolean;
  theme?: BUITheme;
  pageTheme?: BUITheme;
  position?: "sticky" | "fixed";
  bgColor?: string;
  switchTheme?: (theme: BUITheme) => void;
  themeLock?: boolean;
  locale: string | undefined;
  hideSignUpAndLogIn?: boolean;
  isDemoTrading?: boolean;
  hideAllMenus?: boolean;
  outerClass?: string;
  wrapperClass?: string;
  appBridge?: HeaderProps["appBridge"];
  /** @see HeaderProps.navUserInfo */
  navUserInfo?: UserInfo | null;
}> = (props) => {
  const {
    apiConfig,
    showTheme,
    theme,
    pageTheme,
    position = "sticky",
    bgColor,
    switchTheme,
    themeLock = false,
    locale,
    hideSignUpAndLogIn,
    hideAllMenus,
    outerClass,
    wrapperClass,
    appBridge
  } = props;

  const { sdkUrl, initHeader } = useNav();

  const changeTheme = (cb: (theme: BUITheme) => void) => {
    const theme = localStorage.getItem("theme") || "light";
    if (theme === "light") {
      cb("dark");
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      switchTheme && switchTheme("dark");
      localStorage.setItem("theme", "dark");
    } else {
      cb("light");
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      switchTheme && switchTheme("light");
      localStorage.setItem("theme", "light");
    }
  };

  const [updateThemeFun, setUpdateThemeFun] = useState<(theme: BUITheme) => void>();

  const headerConfig = {
    hideSignUpAndLogIn: hideSignUpAndLogIn,
    showTheme: showTheme,
    position: position,
    baseUrl: apiConfig.baseUrl,
    theme: theme,
    pageTheme: pageTheme,
    themeLock: themeLock,
    changeTheme: changeTheme,
    locale: locale || "en",
    hideAllMenus: hideAllMenus,
    wrapperClass: wrapperClass,
    appBridge: appBridge,
    /**
     * `createHeader` / SDK `createHeader` re-mount into `#header` via a second root.
     * Omitting this drops injected auth back to legacy `undefined` → `isLogin === null`
     * and the user block disappears after the effect runs.
     */
    navUserInfo: props.navUserInfo
  };

  const initHeaderCallback = () => {
    const remoteHeader = (window as unknown as Record<string, unknown>)[headerName] as
      | {
          createHeader?: (
            config: typeof headerConfig & { env: string }
          ) => Promise<{ updateTheme?: (theme: BUITheme) => void } | null>;
        }
      | undefined;
    const createHeaderRemote = remoteHeader?.createHeader;
    if (typeof createHeaderRemote !== "function") {
      console.error(
        `[blofin-nav] ${headerName}.createHeader is not available — check nav UMD / entry.js`
      );
      return;
    }
    createHeaderRemote({ ...headerConfig, env: "client" }).then((result) => {
      if (result?.updateTheme) {
        setUpdateThemeFun(() => result.updateTheme);
      }
    });
  };

  useEffect(() => {
    if (sdkUrl.header) {
      initHeader(initHeaderCallback);
    } else {
      if (process.env.NODE_ENV === "development") {
        createHeader(headerConfig).then((result) => {
          if (result && result.updateTheme) {
            setUpdateThemeFun(() => result.updateTheme);
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdkUrl.header]);

  useEffect(() => {
    if (theme && updateThemeFun) {
      updateThemeFun(theme);
    }
  }, [theme, updateThemeFun]);

  return (
    <div
      className={`${headerName} h-[56px] w-full overflow-visible ${bgColor ? bgColor : ""} ${outerClass}`}
      id="header"
    >
      <Header {...props} baseUrl={apiConfig.baseUrl} />
    </div>
  );
};

export default NavHeader;
